import React, { useRef, useState, useEffect } from 'react';
import './CamDown.css';
import DecryptedText from '../blocks/DecryptedText/DecryptedText';
import Carousel, { CarouselItem } from '../blocks/Carousel/Carousel';
import AnimatedList from '../blocks/AnimatedList/AnimatedList';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import { CSSProperties } from 'react';
import { detectBrowserLanguage, getTranslations, Language } from "../utils/localization";

// Video player styles
const videoContainerStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: '8px',
  marginTop: '1rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.5s ease-in-out',
};

const videoCinematicStyle: CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: '1200px',
  height: 'auto',
  zIndex: 1000,
  overflow: 'hidden',
  borderRadius: '8px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
};

const videoOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  zIndex: 999,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.3s ease, visibility 0.3s ease',
};

const closeBtnStyle: CSSProperties = {
  position: 'absolute',
  top: '15px',
  right: '15px',
  color: 'white',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  border: 'none',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  zIndex: 1001,
  fontSize: '24px',
};

// Upcoming video message style
const upcomingVideoStyle: CSSProperties = {
  textAlign: 'center',
  margin: '15px 0',
  fontSize: '1.2rem',
  fontWeight: 500,
  color: '#266867',
  fontStyle: 'italic',
};

const languageSwitcherStyle: CSSProperties = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1000,
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden'
};

// Spotlight toggle button styles
const spotlightToggleStyle: CSSProperties = {
  position: 'fixed',
  bottom: '20px',
  left: '20px',
  zIndex: 1000,
  background: 'rgba(38, 104, 103, 0.2)',
  border: 'none',
  borderRadius: '50%',
  width: '50px',
  height: '50px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
};

// Spotlight overlay styles
const spotlightOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 900,
  transition: 'opacity 0.3s ease',
};

const CamDown: React.FC = () => {
  // Video player state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isStandardVideoPlaying, setIsStandardVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const standardVideoRef = useRef<HTMLVideoElement>(null);
  const videoOverlayRef = useRef<HTMLDivElement>(null);
  
  // Spotlight effect state
  const [spotlightEnabled, setSpotlightEnabled] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const spotlightRef = useRef<HTMLDivElement>(null);
  const lampRef = useRef<HTMLImageElement>(null);
  const lampContainerRef = useRef<HTMLDivElement>(null);

  // Language state
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState(getTranslations('en').camDown);

  // Detect browser language on component mount
  useEffect(() => {
    const detectedLanguage = detectBrowserLanguage();
    setLanguage(detectedLanguage);
    setTranslations(getTranslations(detectedLanguage).camDown);
  }, []);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
    setTranslations(getTranslations(lang).camDown);
  };

  // Handle video play/pause
  const handleVideoPlay = () => {
    if (!isVideoPlaying) {
      // Pause the standard video first
      if (standardVideoRef.current) {
        standardVideoRef.current.pause();
      }
      
      setIsVideoPlaying(true);
      setIsStandardVideoPlaying(true);
      if (videoOverlayRef.current) {
        videoOverlayRef.current.style.opacity = '1';
        videoOverlayRef.current.style.visibility = 'visible';
        
        // Make sure the video in cinema mode plays
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play().catch(e => console.error("Could not play video:", e));
          }
        }, 300); // Small delay to allow the transition to complete
      }
    }
  };

  const handleVideoClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsVideoPlaying(false);
    // Don't reset isStandardVideoPlaying when exiting cinema mode
    // This ensures the play overlay remains hidden when returning to normal view
    if (videoOverlayRef.current) {
      videoOverlayRef.current.style.opacity = '0';
      videoOverlayRef.current.style.visibility = 'hidden';
    }
  };

  const handleStandardVideoPlay = () => {
    setIsStandardVideoPlaying(true);
    handleVideoPlay();
  };

  const handleStandardVideoPause = () => {
    setIsStandardVideoPlaying(false);
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle ESC key to exit cinematic mode
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVideoPlaying) {
        handleVideoClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isVideoPlaying]);

  const sectionRefs = {
    video: useRef<HTMLElement>(null),
    behind: useRef<HTMLElement>(null),
    showcase: useRef<HTMLElement>(null),
    gameplay: useRef<HTMLElement>(null),
    attributes: useRef<HTMLElement>(null),
    background: useRef<HTMLElement>(null),
    challenges: useRef<HTMLElement>(null),
    development: useRef<HTMLElement>(null)
  };

  const [activeSection, setActiveSection] = useState<number>(0);

  const scrollToSection = (item: string, index: number) => {
    const refKeys = Object.keys(sectionRefs);
    const ref = sectionRefs[refKeys[index] as keyof typeof sectionRefs];
    
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(index);
    }
  };

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = Object.values(sectionRefs);
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Set a minimum threshold of visibility for a section to be considered active
      const visibilityThreshold = 100; // px from top of viewport
      
      // Check each section
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        if (
          (rect.top >= 0 && rect.top <= visibilityThreshold) ||
          (rect.top <= 0 && rect.bottom > visibilityThreshold)
        ) {
          setActiveSection(i);
          return;
        }
      }
      
      // Fallback approach based on which section occupies most of the viewport
      let maxVisibleSection = 0;
      let maxVisibleHeight = 0;
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        // Calculate how much of the section is visible in the viewport
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(viewportHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        
        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          maxVisibleSection = i;
        }
      }
      
      setActiveSection(maxVisibleSection);
    };
    
    // Throttle the scroll handler
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const throttledScrollHandler = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 50); // 50ms delay
    };
    
    window.addEventListener('scroll', throttledScrollHandler);
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  const showcaseItems: CarouselItem[] = [
    {
      id: 1,
      title: translations.showcase.items.livestream.title,
      description: translations.showcase.items.livestream.description,
      image: "/images/projects/camdown/showCase1.jpg",
      alt: translations.showcase.items.livestream.title
    },
    {
      id: 2,
      title: translations.showcase.items.interactive.title,
      description: translations.showcase.items.interactive.description,
      image: "/images/projects/camdown/showCase2.jpg",
      alt: translations.showcase.items.interactive.title
    },
    {
      id: 3,
      title: translations.showcase.items.gameLoop.title,
      description: translations.showcase.items.gameLoop.description,
      image: "/images/projects/camdown/showCase3.jpg",
      alt: translations.showcase.items.gameLoop.title
    },
    {
      id: 4,
      title: translations.showcase.items.strategy.title,
      description: translations.showcase.items.strategy.description,
      image: "/images/projects/camdown/showCase4.jpg",
      alt: translations.showcase.items.strategy.title
    }
  ];

  // Updated navigation items to include the video section
  const navItems = language === 'zh' 
    ? ['展示视频', ...translations.navItems]
    : ['Game Demo', ...translations.navItems];

  // Lamp rotation based on mouse position - separate from spotlight
  useEffect(() => {
    const handleLampRotation = (e: MouseEvent) => {
      if (lampRef.current && lampContainerRef.current && spotlightEnabled) {
        const lampRect = lampContainerRef.current.getBoundingClientRect();
        const lampCenterX = lampRect.left + lampRect.width / 2;
        const lampBottomY = lampRect.bottom;
        
        // Calculate angle between lamp bottom center and cursor
        const deltaX = e.clientX - lampCenterX;
        const deltaY = e.clientY - lampBottomY;
        
        // Calculate angle and convert to degrees
        // Using the regular calculation, but we'll flip it later
        let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
        
        // Flip the angle completely by adding 180 degrees
        angle = angle + 90;
        
        // Limit rotation angle to a reasonable range
        const limitedAngle = Math.max(-75, Math.min(75, angle));
        
        // Apply rotation with smooth transition
        lampRef.current.style.transform = `rotate(${limitedAngle}deg)`;
      } else if (lampRef.current && !spotlightEnabled) {
        // Reset to straight position when effect is disabled
        lampRef.current.style.transform = 'rotate(0deg)';
      }
    };

    window.addEventListener('mousemove', handleLampRotation);
    
    // Set initial position based on spotlight state
    if (lampRef.current) {
      lampRef.current.style.transform = 'rotate(0deg)';
    }
    
    return () => {
      window.removeEventListener('mousemove', handleLampRotation);
    };
  }, [spotlightEnabled]);

  // Spotlight effect handlers - separate from lamp rotation
  useEffect(() => {
    const handleSpotlightMove = (e: MouseEvent) => {
      if (spotlightEnabled && spotlightRef.current) {
        setMousePosition({ x: e.clientX, y: e.clientY });
      }
    };

    window.addEventListener('mousemove', handleSpotlightMove);
    return () => {
      window.removeEventListener('mousemove', handleSpotlightMove);
    };
  }, [spotlightEnabled]);

  const toggleSpotlight = () => {
    setSpotlightEnabled(!spotlightEnabled);
    
    // Reset lamp position when toggling off
    if (spotlightEnabled && lampRef.current) {
      lampRef.current.style.transform = 'rotate(0deg)';
    }
  };

  // Update spotlight position based on mouse coordinates
  useEffect(() => {
    if (spotlightRef.current && spotlightEnabled) {
      spotlightRef.current.style.background = `radial-gradient(
        circle 600px at ${mousePosition.x}px ${mousePosition.y}px,
        transparent 0%,
        rgba(0, 0, 0, 0.7) 70%
      )`;
      
      // Create a "hole" in the overlay to see through
      spotlightRef.current.style.maskImage = `radial-gradient(
        circle 600px at ${mousePosition.x}px ${mousePosition.y}px,
        transparent 0%,
        black 70%
      )`;
      
      // Support for webkit browsers
      spotlightRef.current.style.webkitMaskImage = `radial-gradient(
        circle 600px at ${mousePosition.x}px ${mousePosition.y}px,
        transparent 0%,
        black 70%
      )`;
    }
  }, [mousePosition, spotlightEnabled]);

  // State to track collisions between side navigation and content
  const [sideNavVisible, setSideNavVisible] = useState(true);
  const sideNavRef = useRef<HTMLDivElement>(null);
  const collisionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check for collisions between side navigation and each section
  useEffect(() => {
    const checkCollision = () => {
      if (!sideNavRef.current) return;
      
      const sideNavRect = sideNavRef.current.getBoundingClientRect();
      let hasCollision = false;
      
      // Add a small margin to detect collision before actual overlap
      const collisionMargin = 20; // 20px margin
      
      // Check collision with each section
      Object.values(sectionRefs).forEach(sectionRef => {
        if (!sectionRef.current) return;
        
        const sectionRect = sectionRef.current.getBoundingClientRect();
        
        // Calculate horizontal overlap with margin
        const horizontalOverlap = !(
          sideNavRect.right < sectionRect.left - collisionMargin || 
          sideNavRect.left > sectionRect.right + collisionMargin
        );
        
        // Calculate vertical overlap (only consider sections in viewport)
        const verticalOverlap = !(
          sectionRect.bottom < 0 ||
          sectionRect.top > window.innerHeight
        );
        
        // If there's both horizontal and vertical overlap, we have a collision
        if (horizontalOverlap && verticalOverlap) {
          hasCollision = true;
        }
      });
      
      setSideNavVisible(!hasCollision);
    };
    
    // Debounced check collision
    const debouncedCheckCollision = () => {
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
      
      collisionTimeoutRef.current = setTimeout(checkCollision, 100);
    };
    
    window.addEventListener('scroll', debouncedCheckCollision);
    window.addEventListener('resize', debouncedCheckCollision);
    
    // Initial check
    checkCollision();
    
    return () => {
      window.removeEventListener('scroll', debouncedCheckCollision);
      window.removeEventListener('resize', debouncedCheckCollision);
      
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
    };
  }, []);

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      {/* Spotlight Overlay */}
      {spotlightEnabled && (
        <div 
          ref={spotlightRef}
          className="spotlight-overlay"
        />
      )}

      {/* Spotlight Toggle Button with Lamp */}
      <button 
        className={`spotlight-toggle ${spotlightEnabled ? 'active' : ''}`}
        onClick={toggleSpotlight}
        aria-label="Toggle spotlight effect"
      >
        <div className="lamp-container" ref={lampContainerRef}>
          <img 
            src="/images/projects/camdown/base.png" 
            alt="Lamp base" 
            className="lamp-base"
          />
          <img 
            ref={lampRef}
            src="/images/projects/camdown/lamp.png" 
            alt="Lamp" 
            className="lamp-light"
          />
        </div>
      </button>

      {/* Video Overlay for Cinematic Mode */}
      <div 
        ref={videoOverlayRef} 
        style={videoOverlayStyle}
        onClick={handleVideoClose}
      >
        {isVideoPlaying && (
          <>
            <button 
              style={closeBtnStyle} 
              onClick={handleVideoClose}
              aria-label="Close video"
            >
              ✕
            </button>
            <div 
              style={videoCinematicStyle} 
              onClick={(e) => e.stopPropagation()}
            >
              <video 
                ref={videoRef}
                controls
                autoPlay
                style={{ width: '100%', display: 'block' }}
                onPause={() => {
                  // Only close if user manually pauses
                  if (document.activeElement === videoRef.current) {
                    handleVideoClose();
                  }
                }}
              >
                <source src="/images/projects/camdown/teaser.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </>
        )}
      </div>

      <div className="project-page camdown-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        {/* Language Switcher */}
        <div style={languageSwitcherStyle}>
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => toggleLanguage('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={() => toggleLanguage('zh')}
          >
            中文
          </button>
        </div>

        <header className="project-header">
          <div className="container">
            <h1>
              <a 
                href="#" 
                onClick={(e) => e.preventDefault()}
              >
                {translations.title}
              </a>
            </h1>
            <hr />
            <h2>
              {translations.subtitle}
            </h2>
          </div>
        </header>

        <main className="project-content">
          <div className="container">
            {/* Video Section - New */}
            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.video}>
                <h3>{navItems[0]}</h3>
                <div 
                  style={videoContainerStyle} 
                  onClick={handleVideoPlay}
                >
                  <video 
                    ref={standardVideoRef}
                    controls
                    style={{ width: '100%', display: 'block', cursor: 'pointer' }}
                    onPlay={handleStandardVideoPlay}
                    onPause={handleStandardVideoPause}
                    poster="/images/projects/camdown/showCase1.jpg"
                    preload="metadata"
                  >
                    <source src="/images/projects/camdown/teaser.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  {!isStandardVideoPlaying && (
                    <div 
                      className="video-play-overlay"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: 'rgba(0,0,0,0.3)',
                        opacity: 1,
                        transition: 'opacity 0.3s ease',
                        cursor: 'pointer',
                        pointerEvents: 'none',
                      }}
                    >
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'rgba(38, 104, 103, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 20px rgba(0,0,0,0.3)',
                      }}>
                        <span style={{
                          borderTop: '15px solid transparent',
                          borderBottom: '15px solid transparent',
                          borderLeft: '25px solid white',
                          marginLeft: '5px',
                        }}></span>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.behind}>
                <h3>{translations.navItems[0]}</h3>
                <p>
                  {translations.sections.behindTheScene}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section showcase-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.showcase}>
                <h3>{translations.showcase.title}</h3>
                <div className="carousel-wrapper">
                  <Carousel 
                    items={showcaseItems}
                    baseWidth={800}
                    autoplay={true}
                    autoplayDelay={5000}
                    pauseOnHover={true}
                    loop={true}
                  />
                </div>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.gameplay}>
                <h3>{translations.navItems[2]}</h3>
                <p>
                  {translations.sections.gameplay}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.attributes}>
                <h3>{translations.navItems[3]}</h3>
                <p>
                  {translations.sections.playerAttributes}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.background}>
                <h3>{translations.navItems[4]}</h3>
                <p>
                  {translations.sections.background}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.challenges}>
                <h3>{translations.navItems[5]}</h3>
                <p>
                  {translations.sections.challenges}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(142, 36, 170, 0.15)"
            >
              <section ref={sectionRefs.development}>
                <h3>{translations.navItems[6]}</h3>
                <p>
                  {translations.sections.development}
                </p>
              </section>
            </SpotlightCard>
          </div>
        </main>

        <div 
          className="side-navigation" 
          ref={sideNavRef} 
          style={{ 
            opacity: sideNavVisible ? 1 : 0,
            visibility: sideNavVisible ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
          }}
        >
          <AnimatedList
            items={navItems}
            onItemSelect={(item, index) => {
              scrollToSection(item, index);
              setActiveSection(index);
            }}
            showGradients={false}
            enableArrowNavigation={true}
            className="camdown-nav"
            displayScrollbar={false}
            selectedIndex={activeSection}
          />
        </div>
      </div>
    </ClickSpark>
  );
};

export default CamDown; 